import cx from 'clsx';
import { dateToInputValue } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { getRuntimeTimezone, MPOST, truncateTimezone } from 'common/helpers.ts';
import { signatorEmptyOrValid } from 'common/validators.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import type { Shipment } from 'types/Shipment.ts';
import { Button } from 'ui/component/Button.tsx';
import { IDelete, IFileUpload } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { Select } from 'ui/control/Select.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useMe } from '#admin/hook/useMe.tsx';
import type { UploadedFile, UploadFailure } from '#admin/hook/useUploadFile.ts';
import { useUploadFile } from '#admin/hook/useUploadFile.ts';

type Props = {
  shipment: Shipment;
  legs: Leg[];
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

const TYPE_OF_DOCUMENT = {
  poc: 'Proof of Collection (POC)',
  pod: 'Proof of Delivery (POD)',
  generic: 'Generic Document',
};

const MAX_FILE_SIZE_MB = 10;

// Multiple files are allowed only for generic documents; POC/POD map to a single collect/deliver event.
const getFilesToUpload = (files: FileList | File[], type: keyof typeof TYPE_OF_DOCUMENT): File[] => {
  const allFiles = Array.from(files);
  if (type === 'generic') return allFiles;
  return allFiles.length > 0 ? [allFiles[0]] : [];
};

export const DocumentUploadDialog: React.FC<Props> = ({ shipment, legs, onSave, onClose, isOpen }) => {
  const { me } = useMe();
  const { t } = useTranslation();
  const finishUploadMutation = useSWRMutation('/leg/finish-upload', MPOST);
  const urlMutation = useSWRMutation('/storage/presigned-url', MPOST);

  const firstLeg = legs[0];

  const leg = React.useMemo(() => {
    const leg = legs.length > 1 ? null : legs[0];
    return leg;
  }, [legs]);

  const [isDragging, setIsDragging] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [fileErrors, setFileErrors] = React.useState<string[]>([]);

  const isMutating = urlMutation.isMutating || finishUploadMutation.isMutating;

  const isManualDeliveryWithoutDocument = !!firstLeg.destination_manual_actual_datetime && !firstLeg.pod_url;

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: false,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      type: isManualDeliveryWithoutDocument ? 'pod' : 'generic',
      signer_name: isManualDeliveryWithoutDocument ? (firstLeg.destination_signer_name ?? '') : '',
      document: [] as File[],
      datetime: isManualDeliveryWithoutDocument ? (dateToInputValue(firstLeg.destination_actual_datetime, firstLeg.destination_tz ?? getRuntimeTimezone()) ?? '') : '',
      comments: isManualDeliveryWithoutDocument ? (firstLeg.destination_comments ?? '') : '',
    },
  });

  const { handleSubmit, formState, watch, getValues, setValue } = formMethods;
  const selectedDocument = watch('document') as File[];
  const comments = watch('comments');
  const signer_name = watch('signer_name');
  const [docType, selectedFile] = watch(['type', 'document']) as [keyof typeof TYPE_OF_DOCUMENT, File[]];

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-fills only when type changes, not on every firstLeg update
  React.useEffect(() => {
    if (docType === 'poc') {
      setValue('signer_name', firstLeg.origin_signer_name ?? '');
      setValue('datetime', dateToInputValue(firstLeg.origin_actual_datetime, firstLeg.origin_tz ?? getRuntimeTimezone()) ?? '');
      setValue('comments', firstLeg.origin_comments ?? '');
    } else if (docType === 'pod') {
      setValue('signer_name', firstLeg.destination_signer_name ?? '');
      setValue('datetime', dateToInputValue(firstLeg.destination_actual_datetime, firstLeg.destination_tz ?? getRuntimeTimezone()) ?? '');
      setValue('comments', firstLeg.destination_comments ?? '');
    } else {
      setValue('signer_name', '');
      setValue('datetime', '');
      setValue('comments', '');
    }

    // Re-gate the current selection to the new type (POC/POD keep a single file) and clear stale errors.
    setValue('document', getFilesToUpload(getValues('document'), docType));
    setFileErrors([]);
  }, [docType]);

  const isDocRequired = docType === 'generic' || (docType === 'poc' && firstLeg.poc_required) || (docType === 'pod' && firstLeg.pod_required);

  const collectDeliverMutation = useSWRMutation(docType === 'poc' ? `/leg/${firstLeg?.id}/collect` : `/leg/${firstLeg?.id}/deliver`, MPOST);

  const origin_tz = leg?.origin_tz || getRuntimeTimezone();
  const destination_tz = leg?.destination_tz || getRuntimeTimezone();

  // Associates the successfully uploaded files to the leg(s). For generic this is a single
  // finish-upload call carrying every URL → one "new documents" notification per leg.
  const addDocument = React.useCallback(
    async (uploadedFiles: UploadedFile[]) => {
      if (uploadedFiles.length === 0) return;

      if (docType === 'poc' || docType === 'pod') {
        const datetime = dayjs.tz(getValues('datetime'), docType === 'poc' ? origin_tz : destination_tz).toISOString();
        await collectDeliverMutation.trigger({ document_url: uploadedFiles[0].url, datetime, manual: true, comments, signer_name });
      } else {
        await finishUploadMutation.trigger({ document_urls: uploadedFiles.map((f) => f.url), leg_ids: legs.map((leg) => leg.id) });
      }
    },
    [finishUploadMutation, legs, getValues, docType, collectDeliverMutation, origin_tz, destination_tz, comments, signer_name],
  );

  const onUploadError = React.useCallback(
    (failures: UploadFailure[]) => {
      setFileErrors(failures.map(({ upload, error }) => t('dialogs.documentUpload.errors.fileUploadFailedNamed', { name: upload.file.name, reason: error.message })));
    },
    [t],
  );

  const { upload, errorMessage: uploadErrorMessage, isUploading } = useUploadFile(me, { onSuccess: addDocument, onError: onUploadError });

  const isLoading = isUploading || isMutating;

  const onSubmit = async ({ type, signer_name }) => {
    if (selectedDocument.length > 0) {
      setFileErrors([]);
      const uploads = selectedDocument.map((file) => ({ type, signerName: signer_name, file, comments }));
      const results = await upload(uploads);
      // onSuccess (addDocument) associated the succeeded files; onError populated fileErrors.
      // Only close when every file succeeded, so partial failures stay visible.
      const allSucceeded = results.every((result) => result.url && !result.error);
      if (allSucceeded && onSave) void onSave();
    } else {
      const datetime = dayjs.tz(getValues('datetime'), type === 'poc' ? origin_tz : destination_tz).toISOString();
      await collectDeliverMutation.trigger({ datetime, manual: true, signer_name, comments });
      if (onSave) void onSave();
    }
  };

  React.useEffect(() => {
    if (uploadErrorMessage) setErrorMessage(uploadErrorMessage);
  }, [uploadErrorMessage]);

  // Shared selection path for both the file input and drag-drop: gate the count by type,
  // then validate each file's size individually so an oversized file doesn't block the others.
  const handleSelectedFiles = (rawFiles: FileList | File[]) => {
    const files = getFilesToUpload(rawFiles, docType);
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const fileSizeMb = file.size / 1024 / 1024;
      if (fileSizeMb > MAX_FILE_SIZE_MB) {
        errors.push(t('dialogs.documentUpload.errors.fileTooLargeNamed', { name: file.name, size: MAX_FILE_SIZE_MB }));
      } else {
        validFiles.push(file);
      }
    }

    // Generic supports multiple files: append to the current selection (de-duped by name+size)
    // so re-opening the picker or dropping more files adds to the list instead of replacing it.
    // POC/POD are single-file, so a new selection replaces the previous one.
    const existing = docType === 'generic' ? selectedDocument : [];
    const merged = [...existing];
    for (const file of validFiles) {
      if (!merged.some((f) => f.name === file.name && f.size === file.size)) merged.push(file);
    }

    setErrorMessage('');
    setFileErrors(errors);
    formMethods.setValue('document', getFilesToUpload(merged, docType));
  };

  const onDropFile = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleSelectedFiles(e.dataTransfer.files);
  };

  const dragStateHandler = (isEntering) => (e) => {
    e.preventDefault();
    setIsDragging(isEntering);
  };

  const handleFileInputChange = (e) => {
    handleSelectedFiles(e.target.files);
  };

  const onDeleteFileAt = (index: number) =>
    formMethods.setValue(
      'document',
      selectedDocument.filter((_, i) => i !== index),
    );

  const handleChooseFile = () => {
    const input = document.getElementById('document');
    if (input) {
      input.click();
    }
  };

  const typeNotUploadedYet = React.useMemo(
    () => (value: string) => {
      if (!leg) return true;
      const doc_already_exists = (value === 'poc' && leg.poc_url) || (value === 'pod' && leg.pod_url);
      return !doc_already_exists;
    },
    [leg],
  );

  const validateSignatorRequired = (value: string) => {
    if (!leg && !value && docType === 'pod') return t('dialogs.documentUpload.errors.signatorRequired');
    if (!value && leg?.pod_required && docType === 'pod') {
      return t('dialogs.documentUpload.errors.signatorRequired');
    }
    return undefined;
  };

  const error =
    getTranslatedError(urlMutation.error, t) || getTranslatedError(finishUploadMutation.error, t) || getTranslatedError(collectDeliverMutation.error, t) || errorMessage;

  const documentTypes = {
    poc: t('dialogs.documentUpload.documentTypes.poc'),
    pod: t('dialogs.documentUpload.documentTypes.pod'),
    generic: t('dialogs.documentUpload.documentTypes.generic'),
  };

  return (
    <Modal key={shipment.id} title={<h4>{t('dialogs.documentUpload.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex max-h-[550px] w-[450px] flex-col gap-4 overflow-y-scroll p-2" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-[100px_min-content] gap-2">
            <span>{t('dialogs.documentUpload.labels.orderNumber')}</span>
            <span>{shipment.logistics_ex_id}</span>
            <span>{t('dialogs.documentUpload.labels.legId')}</span>
            <span>{leg?.logistics_ex_id || t('dialogs.documentUpload.labels.allLegs')}</span>
          </div>

          {leg && (
            <div className="mt-6 flex flex-col gap-2">
              <Select
                name="type"
                className="h-12"
                label={t('dialogs.documentUpload.labels.type')}
                disabled={isLoading}
                options={documentTypes}
                registerOptions={{ required: true, validate: { typeNotUploadedYet } }}
              />
              <div className="red">
                {formState.errors.type?.type === 'required' ? t('dialogs.documentUpload.errors.required') : null}
                {formState.errors.type?.type === 'typeNotUploadedYet' ? t('dialogs.documentUpload.errors.typeAlreadyExists', { type: docType.toUpperCase() }) : null}
              </div>
            </div>
          )}

          {docType !== 'generic' && (
            <>
              <div className="flex flex-col">
                <Input
                  name="datetime"
                  label={
                    (docType === 'poc' ? t('dialogs.documentUpload.labels.collectionDatetime') : t('dialogs.documentUpload.labels.deliveryDatetime')) +
                    ` (${truncateTimezone(docType === 'poc' ? origin_tz : destination_tz)})`
                  }
                  type="datetime-local"
                  disabled={isLoading}
                  registerOptions={{ required: true }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Input
                  name="signer_name"
                  label={t('dialogs.documentUpload.labels.signatorName')}
                  disabled={isLoading}
                  registerOptions={{ required: false, validate: { required: validateSignatorRequired, signatorEmptyOrValid } }}
                />
              </div>
            </>
          )}

          <div className="flex flex-col gap-2">
            <Input name="comments" label={t('dialogs.documentUpload.labels.comments')} disabled={isLoading} registerOptions={{ required: false }} />
          </div>

          {!isLoading && selectedDocument && selectedDocument.length > 0 && (
            <div className="mt-2 flex flex-col gap-2">
              {selectedDocument.map((file, index) => (
                <div key={`${file.name}-${index}`} className="b-1 flex items-center gap-3 border border-gray-300 p-4 hover:ring-2 hover:ring-blue-500">
                  <IFileUpload className="grey" size={32} />
                  <span className="flex-1 break-all">{file.name}</span>
                  <IDelete size={22} className="red cursor-pointer" onClick={() => onDeleteFileAt(index)} />
                </div>
              ))}
            </div>
          )}

          {fileErrors.length > 0 && (
            <div className="flex flex-col gap-1">
              {fileErrors.map((message, index) => (
                <span key={`${message}-${index}`} className="text-red-500 text-sm">
                  {message}
                </span>
              ))}
            </div>
          )}
          <div
            className={cx('mt-6 flex h-32 flex-col items-center justify-center gap-2 border-2 border-gray-300 border-dashed py-8', isDragging && 'border-blue-500')}
            onDragEnter={dragStateHandler(true)}
            onDragOver={dragStateHandler(true)}
            onDrop={onDropFile}
            onDragLeave={dragStateHandler(false)}
          >
            {isLoading && <Spinner />}
            {!isLoading && (
              <>
                <label htmlFor="document" className="flex size-full cursor-pointer items-center justify-center gap-4">
                  {!isDragging && (
                    <>
                      <span className="text-blue-500">{t('dialogs.documentUpload.dropzone.text')}</span>
                      <Button type="button" className="blue-outlined w-max" onClick={handleChooseFile}>
                        {t('dialogs.documentUpload.dropzone.button')}
                      </Button>
                    </>
                  )}
                  {isDragging && <IFileUpload className="blue" size={32} />}
                </label>
                <input disabled={isLoading} type="file" name="document" id="document" className="hidden" multiple={docType === 'generic'} onChange={handleFileInputChange} />
              </>
            )}
          </div>
          <div className="mt-4 flex gap-5">
            <Button disabled={isLoading} className="blue-outlined" onClick={onClose}>
              {t('dialogs.documentUpload.buttons.back')}
            </Button>
            <Button
              type="submit"
              disabled={!formState.isValid || (selectedFile.length === 0 && isDocRequired)}
              loading={isLoading}
              className="blue flex items-center justify-center"
            >
              {t('dialogs.documentUpload.buttons.send')}
            </Button>
          </div>
          {error && <span className="text-center text-red-500">{error}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
